"use client";

import * as React from "react";
import { BadgeCheckIcon, XIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { identityHeaderTint, identityInitialsTint } from "../../lib/identity-tint";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "./dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./hover-card";
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { UserAvatar, getUserInitials } from "./avatar";

export type IdentityPreviewCardSize = "dialog" | "popover" | "hover";

export type IdentityPreviewAvatar = {
  src?: string | null;
  name?: string | null;
  /** Shown instead of the tinted initials when there is no photo (e.g. an icon for a non-person record). */
  fallback?: React.ReactNode;
  fallbackClassName?: string;
};

export type IdentityPreviewCover = {
  /** "warm" (default) = the soft header in the person's initials tint; "none" = a plain header. */
  variant?: "warm" | "none";
  /** Optional content placed on the right of the header. */
  content?: React.ReactNode;
  className?: string;
};

export type IdentityPreviewStat = {
  label: string;
  value: React.ReactNode;
};

export type IdentityPreviewDetail = {
  label: string;
  /** Empty values (null, undefined, false, "") are left out, so callers never render "Not set" rows. */
  value?: React.ReactNode;
  icon?: React.ReactNode;
};

type IdentityPreviewCardProps = {
  size?: IdentityPreviewCardSize;
  /** Kept for compatibility: every layout now shares the same left-aligned tinted header. */
  layout?: "profile" | "detail";
  /** Kept for compatibility (exposed as `data-orientation`): every size uses the left-aligned header. */
  orientation?: "vertical" | "horizontal";
  avatar?: IdentityPreviewAvatar;
  cover?: IdentityPreviewCover;
  title: string;
  subtitle?: React.ReactNode;
  /** Muted secondary line under the subtitle (e.g. an email or phone). Compose your own icon + text. */
  subtitleMeta?: React.ReactNode;
  verification?: React.ReactNode | boolean;
  verificationLabel?: string;
  /** Status pills. The hover card shows only the first one, on the right. */
  badges?: React.ReactNode;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  utilityActions?: React.ReactNode;
  aboutLabel?: React.ReactNode;
  about?: React.ReactNode;
  stats?: IdentityPreviewStat[];
  details?: IdentityPreviewDetail[];
  /** One muted line for the facts that are missing (e.g. "No phone or email · Add"), dialog only. */
  missingSummary?: React.ReactNode;
  extraContent?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
};

type IdentityPreviewDialogProps = Omit<IdentityPreviewCardProps, "size"> & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ariaTitle: string;
  showCloseButton?: boolean;
  contentProps?: Omit<React.ComponentProps<typeof DialogContent>, "children">;
};

type IdentityPreviewPopoverProps = Omit<IdentityPreviewCardProps, "size"> &
  Omit<React.ComponentProps<typeof Popover>, "children"> & {
    children: React.ReactNode;
    /** Show a round close button in the top-right (default false: the popover closes on outside click / Escape). */
    showCloseButton?: boolean;
    triggerProps?: Omit<React.ComponentProps<typeof PopoverTrigger>, "children">;
    contentProps?: Omit<React.ComponentProps<typeof PopoverContent>, "children">;
  };

type IdentityPreviewHoverCardProps = Omit<IdentityPreviewCardProps, "size"> &
  Omit<React.ComponentProps<typeof HoverCard>, "children"> & {
    children: React.ReactNode;
    triggerProps?: Omit<React.ComponentProps<typeof HoverCardTrigger>, "children">;
    contentProps?: Omit<React.ComponentProps<typeof HoverCardContent>, "children">;
  };

const SIZE_STYLES: Record<
  IdentityPreviewCardSize,
  { avatar: string; initials: string; title: string; subtitle: string; header: string; body: string }
> = {
  dialog: {
    avatar: "!size-16",
    initials: "text-[22px]",
    title: "text-[21px] leading-7",
    subtitle: "text-sm",
    header: "gap-3.5 px-5 py-5 pr-14",
    body: "gap-4 px-5 pt-4 pb-5",
  },
  popover: {
    avatar: "!size-12",
    initials: "text-[17px]",
    title: "text-[15.5px] leading-5",
    subtitle: "text-[13px]",
    header: "gap-3 p-4",
    body: "gap-3.5 px-4 pt-3.5 pb-4",
  },
  hover: {
    avatar: "!size-10",
    initials: "text-sm",
    title: "text-sm leading-5",
    subtitle: "text-[12.5px]",
    header: "gap-2.5 px-3.5 py-3",
    body: "",
  },
};

/** The card surface: white, 16px radius, thin border, soft shadow. */
const CARD_SURFACE =
  "overflow-hidden rounded-2xl border border-border/60 bg-card text-card-foreground shadow-[0_18px_44px_-14px_rgba(16,24,40,0.3)] dark:shadow-[0_18px_44px_-14px_rgba(0,0,0,0.6)]";

function DefaultVerification({ label }: { label?: string }) {
  return (
    <span aria-label={label} className='inline-flex size-5 items-center justify-center rounded-full bg-sky-500 text-white'>
      <BadgeCheckIcon className='size-3.5 fill-current stroke-[2.6]' />
    </span>
  );
}

function renderVerification(verification: IdentityPreviewCardProps["verification"], label?: string) {
  if (!verification) return null;
  if (verification === true) return <DefaultVerification label={label} />;
  return verification;
}

/** The pills as a flat list (fragments unwrapped), so the hover card can keep only the first one. */
function flattenNodes(node: React.ReactNode): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  React.Children.forEach(node, (child) => {
    if (child === null || child === undefined || child === false || child === "") return;
    if (React.isValidElement<{ children?: React.ReactNode }>(child) && child.type === React.Fragment) {
      out.push(...flattenNodes(child.props.children));
      return;
    }
    out.push(child);
  });
  return out;
}

function isEmptyValue(value: React.ReactNode): boolean {
  if (value === null || value === undefined || value === false) return true;
  if (typeof value === "string") return value.trim().length === 0;
  return false;
}

function IdentityAvatar({
  avatar,
  name,
  size,
}: {
  avatar?: IdentityPreviewAvatar;
  name: string;
  size: IdentityPreviewCardSize;
}) {
  const styles = SIZE_STYLES[size];
  return (
    <UserAvatar
      className={cn("shrink-0 rounded-full", styles.avatar)}
      name={name}
      src={avatar?.src ?? undefined}
      fallback={avatar?.fallback ?? getUserInitials(name)}
      fallbackClassName={cn("font-extrabold", styles.initials, identityInitialsTint(name), avatar?.fallbackClassName)}
    />
  );
}

function IdentityHeading({
  size,
  title,
  subtitle,
  subtitleMeta,
  verification,
}: {
  size: IdentityPreviewCardSize;
  title: string;
  subtitle?: React.ReactNode;
  subtitleMeta?: React.ReactNode;
  verification: React.ReactNode;
}) {
  const styles = SIZE_STYLES[size];
  return (
    <div className='min-w-0 flex-1'>
      <div className='flex min-w-0 items-center gap-1.5'>
        <h2 className={cn("min-w-0 truncate font-extrabold tracking-[-0.01em] text-foreground", styles.title)}>{title}</h2>
        {verification ? <span className='shrink-0'>{verification}</span> : null}
      </div>
      {subtitle ? <div className={cn("min-w-0 truncate text-muted-foreground", styles.subtitle)}>{subtitle}</div> : null}
      {subtitleMeta ? (
        <div
          className={cn(
            "mt-0.5 flex min-w-0 items-center gap-1.5 text-muted-foreground [&_svg]:size-3.5 [&_svg]:shrink-0",
            styles.subtitle,
          )}
        >
          {subtitleMeta}
        </div>
      ) : null}
    </div>
  );
}

function IdentityPreviewCardView({
  size = "dialog",
  layout = "profile",
  orientation = "vertical",
  avatar,
  cover,
  title,
  subtitle,
  subtitleMeta,
  verification,
  verificationLabel,
  badges,
  primaryAction,
  secondaryAction,
  utilityActions,
  aboutLabel,
  about,
  stats,
  details,
  missingSummary,
  extraContent,
  footer,
  className,
  bare = false,
}: IdentityPreviewCardProps & { bare?: boolean }) {
  const styles = SIZE_STYLES[size];
  const resolvedVerification = renderVerification(verification, verificationLabel);
  const avatarName = avatar?.name || title;
  const tinted = (cover?.variant ?? "warm") !== "none";
  const badgeNodes = flattenNodes(badges);
  const sectionProps = {
    "data-slot": "identity-preview-card",
    "data-size": size,
    "data-layout": layout,
    "data-orientation": orientation,
  };

  if (size === "hover") {
    const pill = badgeNodes[0];
    return (
      <section {...sectionProps} className={cn("w-full", !bare && CARD_SURFACE, className)}>
        <div className={cn("flex min-w-0 items-center", styles.header)}>
          <IdentityAvatar avatar={avatar} name={avatarName} size={size} />
          <IdentityHeading
            size={size}
            title={title}
            subtitle={subtitle ?? subtitleMeta}
            verification={resolvedVerification}
          />
          {pill ? <div className='shrink-0'>{pill}</div> : null}
        </div>
      </section>
    );
  }

  const isDialog = size === "dialog";
  const visibleDetails = isDialog ? (details ?? []).filter((detail) => !isEmptyValue(detail.value)) : [];
  const hasActions = Boolean(primaryAction || secondaryAction || utilityActions);
  const hasStats = isDialog && stats && stats.length > 0;

  return (
    <section {...sectionProps} className={cn("w-full", !bare && CARD_SURFACE, className)}>
      <div
        data-slot='identity-preview-header'
        className={cn("relative flex min-w-0 items-center", styles.header, tinted && identityHeaderTint(avatarName), cover?.className)}
      >
        <IdentityAvatar avatar={avatar} name={avatarName} size={size} />
        <IdentityHeading
          size={size}
          title={title}
          subtitle={subtitle}
          subtitleMeta={subtitleMeta}
          verification={resolvedVerification}
        />
        {cover?.content ? <div className='shrink-0'>{cover.content}</div> : null}
      </div>

      <div className={cn("flex flex-col empty:hidden", styles.body)}>
        {badgeNodes.length > 0 ? <div className='flex flex-wrap items-center gap-1.5'>{badgeNodes}</div> : null}

        {hasActions ? (
          <div className='flex flex-wrap items-center gap-2'>
            {primaryAction}
            {secondaryAction}
            {utilityActions}
          </div>
        ) : null}

        {isDialog && about ? (
          <div className='space-y-1'>
            {aboutLabel ? <div className='text-xs font-bold text-muted-foreground'>{aboutLabel}</div> : null}
            <div className='text-[13.5px] leading-5 text-foreground/80'>{about}</div>
          </div>
        ) : null}

        {isDialog && extraContent ? <div>{extraContent}</div> : null}

        {visibleDetails.length > 0 ? (
          <dl className='divide-y divide-border/60 border-y border-border/60'>
            {visibleDetails.map((detail) => (
              <div key={detail.label} className='flex min-w-0 items-start justify-between gap-4 py-2.5 text-[13.5px]'>
                <dt className='flex shrink-0 items-center gap-1.5 text-muted-foreground [&_svg]:size-3.5'>
                  {detail.icon}
                  {detail.label}
                </dt>
                <dd className='min-w-0 break-words text-right font-semibold text-foreground'>{detail.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {isDialog && missingSummary ? (
          <div className='text-[13px] text-muted-foreground'>{missingSummary}</div>
        ) : null}

        {hasStats ? (
          <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>
            {stats.map((stat) => (
              <div key={stat.label} className='min-w-0 rounded-[14px] bg-muted/60 px-3 py-2.5'>
                <div className='truncate text-[11.5px] font-bold text-muted-foreground'>{stat.label}</div>
                <div className='mt-0.5 truncate text-[15px] font-extrabold text-foreground'>{stat.value}</div>
              </div>
            ))}
          </div>
        ) : null}

        {isDialog && footer ? <div>{footer}</div> : null}
      </div>
    </section>
  );
}

/**
 * The shared person / identity card (board P2): a soft header in the person's initials tint (fading to the card
 * colour, left to right) with the avatar (photo or tinted initials) and the name + role, left-aligned; then status
 * pills and pill actions. `dialog` adds the note, the facts as label / value rows and stats tiles; `hover` is the
 * compact quick look (avatar, name, role, first pill on the right).
 */
export function IdentityPreviewCard(props: IdentityPreviewCardProps) {
  return <IdentityPreviewCardView {...props} />;
}

/** Shared className that strips a Radix overlay surface back to bare transparency so the card is the only chrome. */
const PREVIEW_CONTENT_RESET = "border-0 bg-transparent p-0 shadow-none ring-0";

/** The round muted close button (top right) shared by the dialog and the optional popover close. */
function PreviewCloseButton({ className, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <Button
      type='button'
      variant='ghost'
      size='icon-sm'
      className={cn(
        "absolute right-3 top-3 z-30 size-8 rounded-full border-0 bg-muted text-muted-foreground shadow-none hover:bg-muted/80 hover:text-foreground",
        className,
      )}
      {...props}
    >
      <XIcon className='size-3.5' />
      <span className='sr-only'>Close</span>
    </Button>
  );
}

export function IdentityPreviewDialog({
  open,
  onOpenChange,
  ariaTitle,
  showCloseButton = true,
  contentProps,
  ...cardProps
}: IdentityPreviewDialogProps) {
  const { className: contentClassName, bodyClassName, ...restContentProps } = contentProps ?? {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        mobileHandle={false}
        className={cn("gap-0 sm:max-w-[min(30rem,calc(100vw-2rem))]", contentClassName)}
        bodyClassName={cn("block p-0", bodyClassName)}
        {...restContentProps}
      >
        <div className='relative'>
          {/* The visible heading is the card title; the dialog title stays for screen readers only. */}
          <DialogTitle className='sr-only'>{ariaTitle}</DialogTitle>
          {showCloseButton ? (
            <DialogClose asChild>
              <PreviewCloseButton />
            </DialogClose>
          ) : null}
          <IdentityPreviewCardView size='dialog' bare {...cardProps} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function IdentityPreviewPopover({
  children,
  showCloseButton = false,
  triggerProps,
  contentProps,
  open,
  defaultOpen,
  onOpenChange,
  modal,
  ...cardProps
}: IdentityPreviewPopoverProps) {
  const { className: contentClassName, ...restContentProps } = contentProps ?? {};

  return (
    <Popover open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
      <PopoverTrigger asChild {...triggerProps}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        sideOffset={8}
        align='start'
        collisionPadding={12}
        className={cn("w-[min(23.75rem,calc(100vw-1.5rem))] max-w-none", PREVIEW_CONTENT_RESET, contentClassName)}
        {...restContentProps}
      >
        <div className='relative'>
          {showCloseButton ? (
            <PopoverClose asChild>
              <PreviewCloseButton />
            </PopoverClose>
          ) : null}
          <IdentityPreviewCardView size='popover' {...cardProps} />
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Which interactive surface the preview renders as. Maps 1:1 to the three wrapper
 * components (and to `IdentityPreviewCard`'s `size`): a modal dialog, a click-anchored
 * popover, or a hover-card.
 */
export type IdentityPreviewVariant = IdentityPreviewCardSize;

type IdentityPreviewProps =
  | ({ variant: "dialog" } & IdentityPreviewDialogProps)
  | ({ variant: "popover" } & IdentityPreviewPopoverProps)
  | ({ variant: "hover" } & IdentityPreviewHoverCardProps);

/**
 * Unified entry point: pick the behavior with a single `variant` prop and this renders the
 * matching wrapper. The remaining props are typed per variant (a discriminated union), so the
 * dialog variant requires `open`/`onOpenChange` while the popover/hover variants require a trigger
 * `children`. Reach for the explicit `IdentityPreviewDialog`/`Popover`/`HoverCard` components when
 * you want the narrowest possible API at the call site.
 */
export function IdentityPreview(props: IdentityPreviewProps) {
  switch (props.variant) {
    case "dialog": {
      const { variant: _variant, ...rest } = props;
      return <IdentityPreviewDialog {...rest} />;
    }
    case "popover": {
      const { variant: _variant, ...rest } = props;
      return <IdentityPreviewPopover {...rest} />;
    }
    case "hover": {
      const { variant: _variant, ...rest } = props;
      return <IdentityPreviewHoverCard {...rest} />;
    }
  }
}

export function IdentityPreviewHoverCard({
  children,
  triggerProps,
  contentProps,
  open,
  defaultOpen,
  onOpenChange,
  openDelay,
  closeDelay,
  ...cardProps
}: IdentityPreviewHoverCardProps) {
  const { className: contentClassName, ...restContentProps } = contentProps ?? {};

  return (
    <HoverCard
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      openDelay={openDelay}
      closeDelay={closeDelay}
    >
      <HoverCardTrigger asChild {...triggerProps}>
        {children}
      </HoverCardTrigger>
      <HoverCardContent
        sideOffset={8}
        align='start'
        collisionPadding={12}
        className={cn("w-[min(20rem,calc(100vw-1.5rem))] max-w-none", PREVIEW_CONTENT_RESET, contentClassName)}
        {...restContentProps}
      >
        <IdentityPreviewCardView size='hover' {...cardProps} />
      </HoverCardContent>
    </HoverCard>
  );
}
